import { FC, useEffect } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { checkAHAVI, replaceHtmlToStr } from 'utils/utils';
import { ClaudeProposalHotelInfo } from 'ts/shared/interfaces/ProposalData';
import {
  ClaudeHotelInfoConfig,
  formatClaudeTextValue,
  getClaudeContentByLanguage,
  getClaudeI18nLanguage,
} from 'utils/shared/claudeProposalInformation';
import { i18nClaude } from 'utils/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';

interface ProposalHtlClaudeHotelInformationProps {
  aI_HotelInformation?: ClaudeProposalHotelInfo;
  config?: ClaudeHotelInfoConfig;
}

const ProposalHtlClaudeHotelInformation: FC<ProposalHtlClaudeHotelInformationProps> = ({
  aI_HotelInformation,
  config,
}) => {
  const { t, i18n: claudeI18n } = useTranslation(undefined, { i18n: i18nClaude });
  useEffect(() => {
    if (config?.language) {
      claudeI18n.changeLanguage(getClaudeI18nLanguage(config.language));
    }
  }, [config?.language, claudeI18n]);

  if (!config || !aI_HotelInformation?.content?.length) {
    return null;
  }

  const hasAnyChecked =
    config.details || config.overview || config.prosCons || !!config.imagesIndexes?.length;

  if (!hasAnyChecked) {
    return null;
  }

  const content = getClaudeContentByLanguage(aI_HotelInformation, config.language);

  if (!content) {
    return null;
  }

  const checkIn = formatClaudeTextValue(content.policies?.checkin);
  const checkOut = formatClaudeTextValue(content.policies?.checkout);
  const pets = formatClaudeTextValue(content.policies?.pets);
  const cancellation = formatClaudeTextValue(content.policies?.cancellation);
  const adultsOnly = formatClaudeTextValue(content.policies?.adultsOnly);
  const summary = content.summary || '';
  const selectedImages = (content.images || []).filter((_, index) =>
    config.imagesIndexes?.includes(index),
  );
  const isRtl = getClaudeI18nLanguage(config.language) === 'he';

  const renderTextSection = (label: string, value: string) => {
    if (!value) {
      return null;
    }

    return (
      <div className="pr-hotel-info-section">
        <div className="pr-hotel-info-label">{label}</div>
        <div
          className={'pr-hotel-info-text' + (checkAHAVI(value) ? ' pr-rtl' : ' pr-ltr')}
          dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(value) }}
        />
      </div>
    );
  };

  const renderListSection = (label: string, items?: string[]) => {
    if (!items?.length) {
      return null;
    }

    return (
      <div className="pr-hotel-info-section">
        <div className="pr-hotel-info-label">{label}</div>
        <div className="pr-hotel-info-facilities row">
          {items.map((item, index) => (
            <div key={index} className={`col-4 ${checkAHAVI(item) ? 'pr-rtl' : 'pr-ltr'}`}>
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const hasDetails =
    config.details &&
    (!!content.name ||
      !!content.location?.address ||
      !!content.location?.area ||
      !!checkIn ||
      !!checkOut ||
      !!pets ||
      !!cancellation ||
      !!adultsOnly ||
      !!content.contact?.phone ||
      !!content.contact?.website ||
      !!content.rating?.overall_10 ||
      !!content.amenities?.length);

  const hasOverview = config.overview && (!!summary || !!content.notes?.length);
  const hasProsCons =
    config.prosCons && (!!content.pros?.length || !!content.cons?.length);
  const hasImages = !!selectedImages.length;

  if (!hasDetails && !hasOverview && !hasProsCons && !hasImages) {
    return null;
  }

  return (
    <div className="pr-hotel-div" dir={isRtl ? 'rtl' : 'ltr'}>
      <I18nextProvider i18n={i18nClaude}>
        <div className="pr-ta-title">
          <FontAwesomeIcon icon={faRobot} style={{ margin: '0 5px', width: 20, height: 20 }} /> {t('hotel.info')}
        </div>
        {hasDetails && (
          <div className="pr-break-avoid pr-hotel-info-block">
            <div className="pr-hotel-title">{t('hotel.details')}</div>
            {renderTextSection(t('hotel.name'), content.name)}
            {renderTextSection(t('hotel.address'), content.location?.address)}
            {renderTextSection(t('hotel.area'), content.location?.area)}
            {renderTextSection(t('search.check_in'), checkIn)}
            {renderTextSection(t('search.check_out'), checkOut)}
            {renderTextSection(t('claude.pets'), pets)}
            {renderTextSection(t('claude.cancellation'), cancellation)}
            {renderTextSection(t('claude.adults_only'), adultsOnly)}
            {renderTextSection(t('hotel.phone'), content.contact?.phone)}
            {renderTextSection(t('claude.website'), content.contact?.website)}
            {!!content.rating?.overall_10 && (
              <div className="pr-hotel-info-section">
                <div className="pr-hotel-info-label">{t('hotel.rating')}</div>
                <div className="pr-hotel-info-text">
                  {content.rating.overall_10}/10
                  {!!content.rating.totalReviews &&
                    ` (${content.rating.totalReviews} ${t('hotel.reviews')})`}
                </div>
              </div>
            )}
            {renderListSection(t('hotel.facilities'), content.amenities)}
          </div>
        )}

        {hasImages && (
          <div className="pr-break-avoid pr-hotel-info-block">
            <div className="pr-hotel-title">{t('hotel.images')}</div>
            <div className="pr-hotel-info-images">
              {selectedImages.map((image, index) => (
                <div className="pr-hotel-info-image" key={index}>
                  <img
                    src={image.link}
                    alt={image.title || ''}
                    onError={(e) => {
                      (e.target as any).parentNode.style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {hasOverview && (
          <div className="pr-break-avoid pr-hotel-info-block">
            <div className="pr-hotel-title">{t('hotel.overview')}</div>
            {!!summary && (
              <div
                className={'pr-hotel-info-text mb-20' + (checkAHAVI(summary) ? ' pr-rtl' : ' pr-ltr')}
                dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(summary) }}
              />
            )}
            {!!content.notes?.length && (
              <div className="pr-hotel-info-section">
                <div className="pr-hotel-info-label">{t('claude.notes')}</div>
                <div className="pr-hotel-info-facilities">
                  {content.notes.map((note, index) => (
                    <div key={index} className={checkAHAVI(note) ? 'pr-rtl' : 'pr-ltr'}>
                      {note}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {hasProsCons && (
          <div className="pr-break-avoid pr-hotel-info-block">
            {!!content.pros?.length && (
              <>
                <div className="pr-hotel-title">{t('claude.pros')}</div>
                <div className="pr-hotel-info-facilities mb-20">
                  {content.pros.map((item, index) => (
                    <div key={index} className={checkAHAVI(item) ? 'pr-rtl' : 'pr-ltr'}>
                      {item}
                    </div>
                  ))}
                </div>
              </>
            )}
            {!!content.cons?.length && (
              <>
                <div className="pr-hotel-title">{t('claude.cons')}</div>
                <div className="pr-hotel-info-facilities">
                  {content.cons.map((item, index) => (
                    <div key={index} className={checkAHAVI(item) ? 'pr-rtl' : 'pr-ltr'}>
                      {item}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </I18nextProvider>
    </div>
  );
};

export default ProposalHtlClaudeHotelInformation;
